/**
 * Especial → SESSP → Gestão de Faturas (#MT-9B4E512) — Fases 1–3.
 * Fatura = saída / pedido SES. SLA = conclusão no armazém (não entrega).
 * Sem origem Farmanet/GSNET neste recorte.
 */
import api from '../api.js';
import { notify } from '../../versa/msgbox.js';
import { fetchEspecialAcl, hasEspecialTenant } from '../utils/especial-acl.js';
import { escapeHtml as esc } from '../utils/escape-html.js';
import { formatBiHttpError } from './bi-http-error.js';

const MAX_DAYS = 93;

const STATUS_OPTS = [
	{ value: '', label: 'Todos' },
	{ value: '10', label: 'Criada' },
	{ value: '20', label: 'Separando' },
	{ value: '30', label: 'Separada' },
	{ value: '40', label: 'Conferindo' },
	{ value: '50', label: 'Unpick' },
	{ value: '60', label: 'Conferida' },
	{ value: '65', label: 'Faturando' },
	{ value: '70', label: 'Faturada' },
	{ value: '75', label: 'Doca Expedição' },
	{ value: '80', label: 'Despachando' },
	{ value: '90', label: 'Concluída' },
	{ value: '100', label: 'Cancelada' },
];

function $(sel, root = document) {
	return root.querySelector(sel);
}

function defaultDateFirstOfMonth() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function defaultToday() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseIsoDate(s) {
	if (!s) return null;
	const d = new Date(`${s}T00:00:00`);
	return Number.isNaN(d.getTime()) ? null : d;
}

function inclusiveDays(fromStr, toStr) {
	const from = parseIsoDate(fromStr);
	if (!from) return 0;
	const to = parseIsoDate(toStr) || new Date();
	to.setHours(0, 0, 0, 0);
	from.setHours(0, 0, 0, 0);
	return Math.floor((to - from) / 86400000) + 1;
}

function money(n) {
	const v = Number(n);
	if (!Number.isFinite(v)) return 'R$ 0,00';
	return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function qty(n) {
	const v = Number(n);
	if (!Number.isFinite(v)) return '0';
	return v.toLocaleString('pt-BR');
}

function noPermHtml() {
	return `
		<div class="title">Gestão de Faturas</div>
		<p class="mts" style="max-width: 40rem;">
			Seu usuário não tem permissão para o módulo <strong>Especial → SESSP</strong>.
			Peça ao administrador para incluir o endpoint
			<strong>GET /especial/sessp</strong> no grupo (grupo <strong>ESPECIAL SESSP</strong>
			ou equivalente).
		</p>
	`;
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

async function loadPrograms(selectEl, companyId) {
	selectEl.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	try {
		const res = await api.list_programs_by_company(companyId);
		if (!res.ok) return;
		const list = await res.json();
		const arr = Array.isArray(list) ? list : [];
		for (const p of arr) {
			const o = document.createElement('option');
			o.value = p.id;
			o.textContent = p.name || `Programa ${p.id}`;
			selectEl.appendChild(o);
		}
	} catch {
		/* lista de programas é auxiliar */
	}
}

function fillStatusSelect(selectEl) {
	selectEl.innerHTML = '';
	for (const opt of STATUS_OPTS) {
		const o = document.createElement('option');
		o.value = opt.value;
		o.textContent = opt.label;
		selectEl.appendChild(o);
	}
}

function maxBar(rows, key) {
	let m = 0;
	for (const r of rows || []) {
		const n = Number(r[key] || 0);
		if (n > m) m = n;
	}
	return m || 1;
}

function renderBars(container, rows, labelKey, valueKey) {
	if (!rows?.length) {
		container.innerHTML = '<p class="muted">Sem dados no período.</p>';
		return;
	}
	const max = maxBar(rows, valueKey);
	let html = '<ul class="efat-bars">';
	for (const r of rows) {
		const n = Number(r[valueKey] || 0);
		const pct = Math.max(2, Math.round((n / max) * 100));
		html += `<li><span class="efat-bar-lab">${esc(r[labelKey])}</span>
			<span class="efat-bar-track"><span class="efat-bar-fill" style="width:${pct}%"></span></span>
			<span class="efat-bar-n">${qty(n)}</span></li>`;
	}
	html += '</ul>';
	container.innerHTML = html;
}

function renderKpis(host, tot) {
	const t = tot || {};
	host.innerHTML = `
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.faturas)}</span><span>Saídas</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.itens)}</span><span>Itens</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.qtd_pedida)}</span><span>Qtd pedida</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.qtd_separada)}</span><span>Qtd separada</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${esc(money(t.valor_pedido))}</span><span>Valor (pedido)</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.sla_dentro)}</span><span>SLA dentro</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.sla_fora)}</span><span>SLA fora</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.sla_andamento)}</span><span>SLA em andamento</span></div>
		<div class="efat-kpi"><span class="efat-kpi-n">${qty(t.sem_drs)}</span><span>Sem DRS</span></div>
	`;
}

function renderTable(host, rows) {
	if (!rows?.length) {
		host.innerHTML = '<p class="muted mts">Nenhuma saída no período (ajuste filtros ou permissões).</p>';
		return;
	}
	let html = `<div class="efat-scroll mts"><table class="view fsx">
		<thead><tr>
			<th></th><th>Saída</th><th>Pedido</th><th>Referência</th><th>Cliente</th>
			<th>Programa</th><th>DRS</th><th>Status</th><th>SLA WMS</th><th>Horas</th>
			<th>Itens</th><th>Qtd pedida</th>
			<th>Qtd sep.</th><th>Valor</th><th>Criação</th>
		</tr></thead><tbody>`;
	for (const r of rows) {
		html += `<tr data-saida="${esc(r.saida_id)}">
			<td>
				<button type="button" class="button efat-open" data-saida="${esc(r.saida_id)}">Itens</button>
				<button type="button" class="button efat-hist" data-saida="${esc(r.saida_id)}">Histórico</button>
			</td>
			<td>${esc(r.saida_id)}</td>
			<td>${esc(r.pedido_id ?? '')}</td>
			<td>${esc(r.referencia)}</td>
			<td>${esc(r.cliente)}</td>
			<td>${esc(r.programa)}</td>
			<td>${esc(r.drs_code ? `${r.drs_code} ${r.drs}` : r.drs)}</td>
			<td>${esc(r.saida_status_label)}</td>
			<td>${esc(r.sla_label)}</td>
			<td>${r.horas_decorridas == null ? '' : qty(r.horas_decorridas)}</td>
			<td>${qty(r.itens)}</td>
			<td>${qty(r.qtd_pedida)}</td>
			<td>${qty(r.qtd_separada)}</td>
			<td>${esc(money(r.valor_pedido))}</td>
			<td>${esc(r.criada_em)}</td>
		</tr>
		<tr class="efat-detail" data-detail-for="${esc(r.saida_id)}" hidden>
			<td colspan="15"><div class="efat-detail-box">Carregando…</div></td>
		</tr>`;
	}
	html += '</tbody></table></div>';
	host.innerHTML = html;
}

function csvEscape(v) {
	const s = v == null ? '' : String(v);
	if (/[",\n;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
	return s;
}

function downloadListaCsv(data) {
	const rows = data?.faturas || [];
	const head = [
		'Saida', 'Pedido', 'Referencia', 'Cliente', 'Programa', 'DRS', 'Status',
		'SLA_WMS', 'Horas', 'Itens', 'Qtd_pedida', 'Qtd_separada', 'Valor_pedido', 'Criacao',
	];
	const lines = [head.join(';')];
	for (const r of rows) {
		lines.push([
			r.saida_id, r.pedido_id, r.referencia, r.cliente, r.programa,
			r.drs, r.saida_status_label, r.sla_label, r.horas_decorridas,
			r.itens, r.qtd_pedida, r.qtd_separada,
			r.valor_pedido, r.criada_em,
		].map(csvEscape).join(';'));
	}
	const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = 'gestao-faturas-sessp.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

export default async function especial_sessp_faturas() {
	const work = document.getElementById('workarea');
	if (!work) return;
	const acl = await fetchEspecialAcl();
	if (!hasEspecialTenant('sessp', acl)) {
		work.innerHTML = noPermHtml();
		return;
	}

	work.innerHTML = `
		<style>
			.efat-hint { color:#555; font-size:.9rem; max-width:52rem; }
			.efat-filters { display:flex; flex-wrap:wrap; gap:.75rem 1rem; align-items:flex-end; margin-top:.75rem; }
			.efat-filters label { display:flex; flex-direction:column; gap:.25rem; font-size:.85rem; }
			.efat-filters select, .efat-filters input[type=date] { min-width:10rem; }
			.efat-check { display:flex; align-items:center; gap:.4rem; padding-bottom:.15rem; }
			.efat-kpis { display:flex; flex-wrap:wrap; gap:.75rem; margin-top:1rem; }
			.efat-kpi { background:#f4f6f8; border:1px solid #dbe1e8; border-radius:6px; padding:.65rem .9rem; min-width:8.5rem; }
			.efat-kpi span { display:block; font-size:.75rem; color:#555; }
			.efat-kpi-n { font-size:1.15rem !important; font-weight:700; color:#1a1a1a !important; }
			.efat-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(16rem,1fr)); gap:1rem; margin-top:1rem; }
			.efat-card { border:1px solid #dbe1e8; border-radius:6px; padding:.75rem 1rem; background:#fff; }
			.efat-card h3 { margin:0 0 .5rem; font-size:.95rem; }
			.efat-bars { list-style:none; margin:0; padding:0; }
			.efat-bars li { display:grid; grid-template-columns:7.5rem 1fr 3rem; gap:.4rem; align-items:center; margin:.25rem 0; font-size:.8rem; }
			.efat-bar-lab { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
			.efat-bar-track { background:#e8eef4; height:.55rem; border-radius:4px; overflow:hidden; }
			.efat-bar-fill { display:block; height:100%; background:#3b6d9c; }
			.efat-bar-n { text-align:right; }
			.efat-scroll { overflow:auto; max-height:55vh; }
			.efat-detail-box { padding:.5rem .75rem; background:#f8fafc; }
			.efat-actions { display:flex; flex-wrap:wrap; gap:.5rem; margin-top:.75rem; }
			button.efat-open, button.efat-hist { font-size:.75rem; padding:.15rem .45rem; }
		</style>
		<div class="title">SESSP — Gestão de Faturas</div>
		<p class="mts efat-hint">
			Visão operacional das <strong>saídas</strong> no período (criação, fuso Brasília).
			Pedido / referência = fatura SES — <strong>não é a NF-e</strong>.
			<strong>SLA</strong> = prazo até a <strong>conclusão no armazém</strong> (horas corridas desde a criação),
			não entrega na unidade. Origem Farmanet/GSNET fica de fora.
			Turnos: 07:00–11:59 1º · 12:00–16:49 1º e 2º (não soma duas vezes) · 16:50–21:48 2º · 21:49–06:59 3º.
			Máximo <strong>${MAX_DAYS} dias</strong>. Itens também em <strong>BI → Linhas de saída</strong>.
		</p>
		<div class="efat-filters">
			<label>Empresa<sup>*</sup><select id="efat-company"></select></label>
			<label>Data inicial<sup>*</sup><input type="date" id="efat-from"></label>
			<label>Data final<input type="date" id="efat-to"></label>
			<label>Programa<select id="efat-program"><option value="">Todos</option></select></label>
			<label>Status da saída<select id="efat-status"></select></label>
			<label>DRS<select id="efat-drs"><option value="">Todos</option></select></label>
			<label>SLA WMS<select id="efat-sla">
				<option value="">Todos</option>
				<option value="dentro">Dentro do prazo</option>
				<option value="fora">Fora do prazo</option>
				<option value="em_andamento">Em andamento (no prazo)</option>
				<option value="sem_regra">Sem regra</option>
			</select></label>
			<div class="efat-check">
				<input type="checkbox" id="efat-canceled">
				<label for="efat-canceled" style="margin:0;">Incluir canceladas</label>
			</div>
			<button type="button" class="button" id="efat-go">Consultar</button>
		</div>
		<p class="mts muted" id="efat-window-hint"></p>
		<div class="efat-kpis" id="efat-kpis"></div>
		<div class="efat-grid">
			<div class="efat-card"><h3>Por mês</h3><div id="efat-month"></div></div>
			<div class="efat-card"><h3>Por status</h3><div id="efat-bystatus"></div></div>
			<div class="efat-card"><h3>Por programa</h3><div id="efat-byprog"></div></div>
			<div class="efat-card"><h3>Por DRS</h3><div id="efat-bydrs"></div></div>
			<div class="efat-card"><h3>SLA WMS (conclusão no armazém)</h3><div id="efat-bysla"></div></div>
			<div class="efat-card"><h3>Mudanças de status por turno</h3><div id="efat-byshift"></div></div>
			<div class="efat-card"><h3>Tempo médio no status (min)</h3><div id="efat-bydwell"></div></div>
		</div>
		<p class="mts muted" id="efat-hist-aviso"></p>
		<div class="efat-actions">
			<button type="button" class="button" id="efat-csv" disabled>Exportar lista (CSV)</button>
		</div>
		<div id="efat-table"></div>
	`;

	const companyEl = $('#efat-company', work);
	const fromEl = $('#efat-from', work);
	const toEl = $('#efat-to', work);
	const programEl = $('#efat-program', work);
	const statusEl = $('#efat-status', work);
	fromEl.value = defaultDateFirstOfMonth();
	toEl.value = defaultToday();
	fillStatusSelect(statusEl);
	await loadCompanies(companyEl);

	let lastData = null;

	const syncHint = () => {
		const df = fromEl.value?.trim();
		const dt = toEl.value?.trim();
		const hint = $('#efat-window-hint', work);
		if (!df) {
			hint.textContent = `Informe a data inicial. Máximo ${MAX_DAYS} dias.`;
			return;
		}
		const days = inclusiveDays(df, dt);
		if (days > MAX_DAYS) hint.textContent = `Período de ${days} dias excede ${MAX_DAYS} dias.`;
		else hint.textContent = `${days} dia(s) no filtro (máx. ${MAX_DAYS}).`;
	};
	fromEl.addEventListener('change', syncHint);
	toEl.addEventListener('change', syncHint);
	syncHint();

	companyEl.addEventListener('change', async () => {
		await loadPrograms(programEl, companyEl.value?.trim());
	});

	const consultar = async () => {
		const companyId = companyEl.value?.trim();
		if (!companyId) {
			notify('Selecione a empresa.');
			return;
		}
		const df = fromEl.value?.trim();
		if (!df) {
			notify('Informe a data inicial.');
			return;
		}
		const dt = toEl.value?.trim();
		const days = inclusiveDays(df, dt);
		if (days > MAX_DAYS) {
			notify(`Período de ${days} dias excede o máximo de ${MAX_DAYS} dias.`);
			return;
		}
		const sp = new URLSearchParams();
		sp.set('date_from', df);
		if (dt) sp.set('date_to', dt);
		const pid = programEl.value?.trim();
		if (pid) sp.set('program_id', pid);
		const st = statusEl.value?.trim();
		if (st) sp.set('status', st);
		const drs = $('#efat-drs', work)?.value?.trim();
		if (drs) sp.set('drs_id', drs);
		const sla = $('#efat-sla', work)?.value?.trim();
		if (sla) sp.set('sla_bucket', sla);
		if ($('#efat-canceled', work)?.checked) sp.set('include_canceled', '1');
		sp.set('limit', '200');
		try {
			const res = await api.especial_sessp_faturas(companyId, sp.toString());
			const raw = await res.text();
			if (!res.ok) {
				notify(formatBiHttpError(res.status, raw), 'Gestão de Faturas');
				return;
			}
			const data = JSON.parse(raw);
			lastData = data;
			renderKpis($('#efat-kpis', work), data.totals);
			renderBars($('#efat-month', work), data.by_month, 'mes_label', 'faturas');
			renderBars($('#efat-bystatus', work), data.by_status, 'label', 'faturas');
			renderBars($('#efat-byprog', work), data.by_program, 'programa', 'faturas');
			renderBars($('#efat-bydrs', work), data.by_drs, 'drs', 'faturas');
			renderBars($('#efat-bysla', work), data.by_sla, 'label', 'faturas');
			renderBars($('#efat-byshift', work), data.by_shift, 'label', 'eventos');
			renderBars($('#efat-bydwell', work), data.by_status_time, 'label', 'minutos_medios');
			const histAv = $('#efat-hist-aviso', work);
			if (histAv) histAv.textContent = data.historico_aviso || '';
			const drsSel = $('#efat-drs', work);
			if (drsSel && Array.isArray(data.drs_catalog)) {
				const cur = drsSel.value;
				drsSel.innerHTML = '<option value="">Todos</option>';
				for (const d of data.drs_catalog) {
					const o = document.createElement('option');
					o.value = d.id;
					o.textContent = `${d.code || ''} ${d.name || ''}`.trim();
					drsSel.appendChild(o);
				}
				drsSel.value = cur;
			}
			renderTable($('#efat-table', work), data.faturas);
			const csvBtn = $('#efat-csv', work);
			if (csvBtn) csvBtn.disabled = !data.faturas?.length;
			const shown = data.faturas?.length || 0;
			const tot = data.totals?.faturas || 0;
			notify(
				shown < tot
					? `Lista: ${shown} de ${tot} saídas (teto 200). Use os filtros para recortar.`
					: `${tot} saída(s) no período.`,
				'Gestão de Faturas',
			);
		} catch (e) {
			notify(e?.message || 'Falha ao consultar.', 'Gestão de Faturas');
		}
	};

	$('#efat-go', work).addEventListener('click', consultar);
	$('#efat-csv', work).addEventListener('click', () => {
		if (!lastData?.faturas?.length) {
			notify('Consulte antes de exportar.');
			return;
		}
		downloadListaCsv(lastData);
	});

	work.addEventListener('click', async (ev) => {
		const histBtn = ev.target.closest?.('.efat-hist');
		const btn = ev.target.closest?.('.efat-open');
		if ((!btn && !histBtn) || (!work.contains(btn || histBtn))) return;
		const saida = (btn || histBtn).getAttribute('data-saida');
		const companyId = companyEl.value?.trim();
		if (!saida || !companyId) return;
		const detailRow = work.querySelector(`tr.efat-detail[data-detail-for="${saida}"]`);
		if (!detailRow) return;
		const box = detailRow.querySelector('.efat-detail-box');
		const kind = histBtn ? 'hist' : 'itens';
		if (!detailRow.hidden && box?.dataset.loaded === kind) {
			detailRow.hidden = true;
			return;
		}
		detailRow.hidden = false;
		if (box?.dataset.loaded === kind) return;
		box.dataset.loaded = '';
		box.textContent = kind === 'hist' ? 'Carregando histórico…' : 'Carregando itens…';
		try {
			if (kind === 'hist') {
				const sp = new URLSearchParams({ output_id: saida });
				const res = await api.especial_sessp_faturas_historico(companyId, sp.toString());
				const raw = await res.text();
				if (!res.ok) {
					box.textContent = formatBiHttpError(res.status, raw);
					return;
				}
				const data = JSON.parse(raw);
				const evs = Array.isArray(data.eventos) ? data.eventos : [];
				if (!evs.length) {
					box.textContent = 'Sem histórico para esta saída.';
					box.dataset.loaded = kind;
					return;
				}
				const fonte = data.fonte === 'proxy'
					? '<p class="muted">Ainda sem log gravado — horários aproximados (criação / separação / conferência / conclusão).</p>'
					: '';
				let h = `${fonte}<table class="view fsx"><thead><tr><th>Quando</th><th>Status</th><th>Turno</th><th>Min. até o próximo</th></tr></thead><tbody>`;
				for (const it of evs) {
					h += `<tr>
						<td>${esc(it.changed_at)}</td>
						<td>${esc(it.new_status_label)}</td>
						<td>${esc(it.turno)} ${esc(it.turno_janela)}</td>
						<td>${it.minutos_ate_proximo == null ? '' : qty(it.minutos_ate_proximo)}</td>
					</tr>`;
				}
				h += '</tbody></table>';
				box.innerHTML = h;
				box.dataset.loaded = kind;
				return;
			}
			const sp = new URLSearchParams({ output_id: saida });
			const res = await api.especial_sessp_faturas_itens(companyId, sp.toString());
			const raw = await res.text();
			if (!res.ok) {
				box.textContent = formatBiHttpError(res.status, raw);
				return;
			}
			const data = JSON.parse(raw);
			const itens = Array.isArray(data.itens) ? data.itens : [];
			if (!itens.length) {
				box.textContent = 'Esta saída não tem itens.';
				box.dataset.loaded = 'itens';
				return;
			}
			let h = '<table class="view fsx"><thead><tr><th>Linha</th><th>SKU</th><th>Produto</th><th>Lote</th><th>Qtd pedida</th><th>Qtd sep.</th><th>Preço un.</th><th>Valor</th></tr></thead><tbody>';
			for (const it of itens) {
				h += `<tr>
					<td>${esc(it.linha)}</td>
					<td>${esc(it.sku)}</td>
					<td>${esc(it.produto)}</td>
					<td>${esc(it.lote)}</td>
					<td>${qty(it.qtd_pedida)}</td>
					<td>${it.qtd_separada == null ? '' : qty(it.qtd_separada)}</td>
					<td>${esc(money(it.preco_unit))}</td>
					<td>${esc(money(it.valor))}</td>
				</tr>`;
			}
			h += '</tbody></table>';
			box.innerHTML = h;
			box.dataset.loaded = 'itens';
		} catch (e) {
			box.textContent = e?.message || (kind === 'hist' ? 'Falha ao carregar histórico.' : 'Falha ao carregar itens.');
		}
	});
}
